import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';

// API Base URL - change this for production
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3030/dev';

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - adds auth token and sysadmin company context to requests
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const authData = localStorage.getItem('auth-storage');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        if (state?.token) {
          config.headers.Authorization = `Bearer ${state.token}`;
        }
      } catch (e) {
        console.error('Error parsing auth data:', e);
      }
    }

    // Inject X-Company-Context header for sysadmin users
    const sysadminData = localStorage.getItem('sysadmin-storage');
    if (sysadminData) {
      try {
        const { state } = JSON.parse(sysadminData);
        if (state?.selectedCompanyId) {
          config.headers['X-Company-Context'] = state.selectedCompanyId;
        }
      } catch (e) {
        console.error('Error parsing sysadmin data:', e);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're currently refreshing the token to prevent multiple refresh requests
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void }> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor - handles errors globally
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      const errorData = error.response?.data as { code?: string };
      
      // Check if it's a token expiration error and we have a refresh token
      if (errorData?.code === 'TOKEN_EXPIRED') {
        const refreshToken = localStorage.getItem('refresh_token');
        
        if (refreshToken) {
          if (isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return api(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }
          
          originalRequest._retry = true;
          isRefreshing = true;
          
          try {
            // Try to refresh the token
            const response = await api.post<{ accessToken: string; refreshToken: string }>(
              '/auth/refresh',
              { refreshToken }
            );
            
            const newToken = response.data.accessToken;
            
            // Update stored tokens
            const authData = localStorage.getItem('auth-storage');
            if (authData) {
              const parsed = JSON.parse(authData);
              parsed.state.token = newToken;
              localStorage.setItem('auth-storage', JSON.stringify(parsed));
            }
            
            if (response.data.refreshToken) {
              localStorage.setItem('refresh_token', response.data.refreshToken);
            }
            
            processQueue(null, newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError as Error, null);
            // Refresh failed - clear everything and redirect to login
            localStorage.removeItem('auth-storage');
            localStorage.removeItem('refresh_token');
            window.location.href = '/login';
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        } else {
          // No refresh token - clear auth and redirect
          localStorage.removeItem('auth-storage');
          window.location.href = '/login';
        }
      }
    }

    // Extract error message from response
    const message =
      (error.response?.data as { error?: string })?.error ||
      (error.response?.data as { message?: string })?.message ||
      error.message ||
      'An unexpected error occurred';

    return Promise.reject(new Error(message));
  }
);

export default api;
